// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// devextreme imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// service imports
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { WorkflowCamStepService } from '../../../workflow/_services/workflow-cam-step.service';
import { WorkflowEditorSharedService } from '../../_services/workflow-editor-shared.service';

// model imports
import { VendorCodingDefaultEditorResultDto } from '../../_models/vendor-coding-default-editor-result-dto';
import { VendorHeaderTemplateRawDto } from '../../_models/vendor-header-template-raw-dto';
import { VendorLineItemCodingTemplateRawDto } from '../../_models/vendor-line-item-coding-template-raw-dto';
import { GLAccountSelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glaccount-selector-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { WFVendorCodingDefaultTemplateParametersDto } from '../../_models/wf-vendor-coding-default-template-parameters-dto';
import { DimensionOptionCodeRawDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/dimensions/dimension-option-code-raw-dto';
import { VendorCodingDefaultEditorParameterDto } from '../../_models/vendor-coding-default-editor-parameter-dto';

// constant imports
import { ActionConstants } from '../../../_constants/action-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';

@Component({
    selector: 'app-workflow-coding-default',
    templateUrl: './workflow-coding-default.component.html',
    styleUrls: ['./workflow-coding-default.component.scss']
})
export class WorkflowCodingDefaultComponent implements OnInit {
    @ViewChild('codingDefaultPopup', { static: false }) codingDefaultPopup: DxPopupComponent;
    @ViewChild('codeEditorLineItemGrid', { static: false }) codeEditorLineItemGrid: DxDataGridComponent;
    @ViewChild('codingDefaultMessageBox', { static: false }) codingDefaultMessageBox: MessageBoxComponent;

    @Input() communityId: number;
    @Input() managementCompanyId: number;
    @Input() workflowId: number;
    @Input() payee: string;
    @Input() payeeCode: string;
    @Input() vendorAccountId: number;
    @Input() workflowStep: string;
    @Input() vendorAccountNumber: string;

    @Output() onClose = new EventEmitter<void>();

    codingDefaultEditor: VendorCodingDefaultEditorResultDto;

    title: string;
    visible = false;
    loadingVisible = false;

    bankAccountColumns: Array<any>;
    bankAccountBoxValue: Array<number>;

    headerTemplate: VendorHeaderTemplateRawDto;
    gridLineItems: Array<VendorLineItemCodingTemplateRawDto> = [];
    glAccountColumns: Array<any> = [];

    currencyFormat: string;

    editorParam: WFVendorCodingDefaultTemplateParametersDto;

    iconPlus = IconConstants.ADD;
    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    iconDelete = IconConstants.DELETE;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly workflowCamStepService: WorkflowCamStepService,
        private readonly workflowEditorSharedService: WorkflowEditorSharedService
    ) {
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit() {
        this.show();

        this.title = Utils.notNullOrEmpty(this.vendorAccountNumber)
            ? `Coding Defaults For ${this.payee} Account #${this.vendorAccountNumber}`
            : `Coding Defaults For ${this.payee}`;

        this.configureDefaults();
        this.populateCodingDefaultEditor();
    }

    show(): void {
        this.visible = true;
    }

    onPopupShown(): void {
        this.codingDefaultPopup.instance.repaint();
    }

    onPopupClose(): void {
        this.closeCodingDefaultEditor();
    }

    onAddNewLineItem(): void {
        this.addNewLineItem();
    }

    onBankAccountSelection(selectedObject: any): void {
        if (selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedBankAccount = selectedObject.selectedRow[0];
            if (Utils.notEquals(selectedBankAccount.bankAccountId, this.headerTemplate.bankAccountId)) {
                this.headerTemplate.bankAccountId = selectedBankAccount.bankAccountId;
            }
        } else {
            this.headerTemplate.bankAccountId = null;
        }
    }

    onSaveClick(): void {
        this.codeEditorLineItemGrid.instance.saveEditData().then(() => {
            this.saveCodingDefaultEditor();
        });
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    onDimensionValueChanged(event: any, cellInfo: any): void {
        cellInfo.setValue(event.value);
    }

    onEnabledValueChanged(event: any, cellInfo: any): void {
        cellInfo.setValue(event.value);
    }

    onDeleteLineItemClick(item: VendorLineItemCodingTemplateRawDto): void {
        if (!item.sequence) {
            this.gridLineItems = this.gridLineItems.filter((d) => Utils.notEquals(d['itemId'], item['itemId']));
        } else {
            item.crudAction = ActionConstants.DELETE;
        }

        this.codeEditorLineItemGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.codeEditorLineItemGrid.instance.refresh();
    }

    onRowUpdated(event: any): void {
        const rowData = event.data;
        if (!!rowData.sequence) {
            rowData.crudAction = ActionConstants.EDIT;
        }
    }

    onGLAccountSelection(account: GLAccountSelectorResultDto[], cellInfo: any): void {
        if (
            Utils.notNullOrEmpty(account) &&
            Utils.notEquals(cellInfo.data.glAccountNumber, account[0].glAccountNumber)
        ) {
            cellInfo.data.glAccountNumber = account[0].glAccountNumber;
            if (Utils.isTrue(this.codingDefaultEditor.entityUsesSubcategory)) {
                this.populateSubcategories(account[0].glAccountNumber, cellInfo.data);
            }
        }
    }

    onSubcategoryValueChanged(event: any, cellInfo: any): void {
        cellInfo.setValue(event.value);
    }

    private populateCodingDefaultEditor(): void {
        const baseParam: VendorCodingDefaultEditorParameterDto = new VendorCodingDefaultEditorParameterDto();
        baseParam.communityId = this.communityId;
        baseParam.workflowId = this.workflowId;
        baseParam.vendorAccountId = this.vendorAccountId;
        baseParam.payeeCode = this.payeeCode;
        baseParam.clearCache = true;

        this.workflowEditorSharedService.getCodingDefaultEditor(baseParam, this.workflowStep).subscribe(
            (result: VendorCodingDefaultEditorResultDto) => {
                this.codingDefaultEditor = result;
                this.workflowEditorSharedService.updateLineItemAvailability(
                    Utils.notNullOrEmpty(result.lineItemTemplates)
                );

                this.prepareLineItems();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private addNewLineItem(): void {
        const newLineItem: VendorLineItemCodingTemplateRawDto = new VendorLineItemCodingTemplateRawDto();
        newLineItem.enabled = true;
        newLineItem.sequence = null;
        newLineItem.description = null;
        newLineItem.itemAmount = 0;
        newLineItem.subAccountId = null;
        newLineItem.dimensions = [];
        newLineItem.crudAction = ActionConstants.ADD;
        newLineItem['itemId'] = this.gridLineItems.length + 1;
        newLineItem['glAccountNUmber'] = null;

        this.gridLineItems.push(newLineItem);
        this.updateLineItemDimensions();
    }

    private prepareLineItems(): void {
        this.gridLineItems = [];
        const gridItems: Array<VendorLineItemCodingTemplateRawDto> = [];
        if (
            Utils.notNullAndDefined(this.codingDefaultEditor) &&
            Utils.notNullOrEmpty(this.codingDefaultEditor.lineItemTemplates)
        ) {
            this.codingDefaultEditor.lineItemTemplates.forEach((lineItem, index) => {
                let obj = new VendorLineItemCodingTemplateRawDto();
                obj = Object.assign(obj, lineItem);
                obj['itemId'] = index;
                obj['glAccountNumber'] = this.codingDefaultEditor.glAccounts.find((d) =>
                    Utils.equals(d.subAccountID, lineItem.subAccountId)
                );

                gridItems.push(obj);
            });
        }
        if (Utils.notNullOrEmpty(gridItems)) {
            this.gridLineItems = gridItems;
            this.updateLineItemDimensions();
        } else {
            this.addNewLineItem();
        }
    }

    private updateLineItemDimensions(): void {
        this.gridLineItems.forEach((lineItem) => {
            if (Utils.notNullOrEmpty(lineItem.dimensions)) {
                lineItem.dimensions.forEach((dim) => {
                    const itemDimension = this.codingDefaultEditor.dimensions.find((d) =>
                        Utils.equalsIgnoreCase(d.code, dim.dimensionCode)
                    );
                    if (!!itemDimension) {
                        lineItem[`${dim.dimensionCode}_source`] = itemDimension.options;
                        const selectedItemDimensionOption = itemDimension.options.find((d) =>
                            Utils.equalsIgnoreCase(d.code, dim.dimensionOptionCode)
                        );
                        if (Utils.notNullAndDefined(selectedItemDimensionOption)) {
                            lineItem[dim.dimensionCode] = selectedItemDimensionOption.code;
                        } else {
                            lineItem[dim.dimensionCode] = null;
                        }
                    }
                });
            }

            if (!!this.codingDefaultEditor && !!this.codingDefaultEditor.dimensions) {
                this.codingDefaultEditor.dimensions.forEach((dim) => {
                    if (!lineItem[`${dim.code}_source`]) {
                        lineItem[`${dim.code}_source`] = dim.options;
                    }
                });
            }
        });
    }

    private populateSubcategories(
        glAccountNumber: string,
        rawItem: VendorLineItemCodingTemplateRawDto,
        clearCache = false
    ): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = this.managementCompanyId;
        baseParam.communityId = this.communityId;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.clearCache = clearCache;
        this.workflowCamStepService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                rawItem['subCategorySource'] = result;

                const subCategory = result.find((d) => Utils.equals(d.id, rawItem.subCategoryId));
                rawItem.subCategoryId = Utils.notNullAndDefined(subCategory) ? subCategory.id : null;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private saveCodingDefaultEditor(): void {
        const modifiedLineItemTemplates = this.getModifiedLineItems();
        const baseParam: WFVendorCodingDefaultTemplateParametersDto = new WFVendorCodingDefaultTemplateParametersDto();
        baseParam.communityId = this.communityId;
        baseParam.workflowId = this.workflowId;
        baseParam.vendorAccountId = this.vendorAccountId;
        baseParam.payeeCode = this.payeeCode;

        baseParam.headerTemplate = this.headerTemplate;
        baseParam.lineItemTemplates = modifiedLineItemTemplates;

        this.loadingVisible = true;

        this.workflowEditorSharedService.saveCodingDefaultEditor(baseParam, this.workflowStep).subscribe(
            () => {
                notify(
                    'Coding line item templates updated successfully.',
                    'success',
                    MessageInterval.successMsgInterval
                );

                this.closeCodingDefaultEditor();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getModifiedLineItems(): Array<VendorLineItemCodingTemplateRawDto> {
        const modifiedLineItems: Array<VendorLineItemCodingTemplateRawDto> = [];
        const dimensionKeysToDelete: string[] = [];

        if (this.gridLineItems && this.gridLineItems.length > 0) {
            for (const lineItem of this.gridLineItems) {
                const rawItem = Object.assign(new VendorLineItemCodingTemplateRawDto(), lineItem);
                if (!!rawItem.crudAction) {
                    if (Utils.notNullOrEmpty(this.codingDefaultEditor.dimensions)) {
                        rawItem.dimensions = [];

                        this.codingDefaultEditor.dimensions.forEach((dim) => {
                            dimensionKeysToDelete.push(dim.code);
                            dimensionKeysToDelete.push(`${dim.code}_source`);

                            const dimensionOption: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                            dimensionOption.dimensionCode = dim.code;
                            dimensionOption.dimensionOptionCode = rawItem[dim.code];
                            if (
                                Utils.notNullOrEmpty(dimensionOption.dimensionCode) &&
                                Utils.notNullOrEmpty(dimensionOption.dimensionOptionCode)
                            ) {
                                rawItem.dimensions.push(dimensionOption);
                            }
                        });
                    }

                    const resultStatus = this.validateRawItem(rawItem);
                    if (Utils.isTrue(resultStatus.isValid)) {
                        dimensionKeysToDelete.forEach((key) => {
                            delete rawItem[key];
                        });
                        delete rawItem['subCategorySource'];
                        delete rawItem['glAccountNUmber'];
                        delete rawItem['itemId'];
                    } else {
                        this.codingDefaultMessageBox.showErrorMessage('Error', resultStatus.message);
                        break;
                    }
                    modifiedLineItems.push(rawItem);
                }
            }
        }

        return modifiedLineItems;
    }

    private validateRawItem(rawItem: VendorLineItemCodingTemplateRawDto): any {
        const resultStatus = { isValid: true, message: null };
        if (!rawItem.subAccountId) {
            resultStatus.isValid = false;
            resultStatus.message = 'GL Account Number cannot be empty.';
        }
        return resultStatus;
    }

    private configureDefaults(): void {
        this.headerTemplate = new VendorHeaderTemplateRawDto();
        this.currencyFormat = this.precisionService.getCurrencyFormat();

        this.bankAccountColumns = [
            {
                dataField: 'accountName',
                dataType: 'string',
                caption: 'Bank Account'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataType: 'string',
                caption: 'GL Dimensions',
                cellTemplate: 'multiDimensionsTemplate'
            },
            {
                dataField: 'isDefault',
                dataType: 'boolean',
                caption: 'Is Default',
                cellTemplate: 'booleanValueCellTemplate'
            },
            {
                dataField: 'displayBankName',
                dataType: 'string',
                visible: false
            }
        ];

        this.glAccountColumns = [
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account No'
            },
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'subAccountID',
                dataType: 'string',
                caption: 'Sub Account Id',
                visible: false
            },
            {
                dataField: 'displayText',
                dataType: 'string',
                caption: 'Display Text',
                visible: false
            }
        ];
    }

    private closeCodingDefaultEditor(): void {
        this.visible = false;
        this.onClose.emit();
    }
}
